import { forwardRef, Suspense, useCallback, useEffect, useRef } from 'react';
import React from 'react';
import { Group, Texture } from 'three';

import { Clone, Environment } from '@react-three/drei';
import { GroupProps } from '@react-three/fiber';
import {
  EdgeProjection,
  EdgeProjectionProps,
  useDollhouse,
  useSpace,
} from '@spazious/dollhouse-engine';
import { useStore as useFSetupStore } from '@spazious/dollhouse-fsetup';
import { useGetVirtualSpaceSuspenseQuery } from '@spazious/dollhouse-graphql/apollo';
import { useGraph, useStore } from '@spazious/dollhouse-store';
import { isMesh } from '@spazious/dollhouse-three';
import { ControlsModeEnum, ViewModeEnum } from '@spazious/dollhouse-types';
import { useForkRef } from '@spazious/react-hooks';

import { Loader } from '../Loader/index.js';

const Projection: React.FC<
  {
    url: string;
  } & EdgeProjectionProps
> = ({ url }) => {
  const { scene } = useSpace(url);
  return <EdgeProjection object={scene} />;
};

/**
 * Space component renders a space from a given url
 */
const InternalSpace = forwardRef<
  Group,
  {
    url: string;
  } & GroupProps
>(({ url }, ref) => {
  const spaceRef = useRef<Group>(null);
  const forkedRef = useForkRef(ref, spaceRef);
  const controls = useStore(({ controls }) => controls);
  const { scene } = useSpace(url);

  const { environmentMap } = scene.userData as {
    environmentMap?: Texture;
  };

  useEffect(() => {
    if (spaceRef.current) {
      spaceRef.current.traverse((child) => {
        if (isMesh(child) && /^ceiling/.test(child.name)) {
          child.visible =
            controls === ControlsModeEnum.Walk || controls === ControlsModeEnum.Travelling;
        }
      });
    }
  }, [controls, spaceRef]);

  return (
    <group>
      <Clone ref={forkedRef} receiveShadow object={scene} />
      {environmentMap && <Environment map={environmentMap} />}
    </group>
  );
});

export const Space: React.FC = () => {
  const setDollhouse = useDollhouse(({ set }) => set);
  const view = useStore(({ view }) => view);
  const world = useStore(({ world }) => world);
  const spaceId = useGraph().graph.shallow.space;

  const { set: setFsetupStore } = useFSetupStore();

  const { data } = useGetVirtualSpaceSuspenseQuery({
    skip: !spaceId,
    variables: {
      id: `${spaceId}`!,
    },
  });

  const { placeholder, model } = data?.virtualSpace ?? {};

  // TODO this is a temporary fix to handle the collider for the camera navigation
  const setScene = useCallback(
    (scene: Group | null) => {
      if (scene) {
        world.setScene(scene);
        // We should defined world at app level
        // @ts-ignore TODO - fix the dollhouse typing should extend from the dollhouse types and three-fiber
        setDollhouse({ world });
        setFsetupStore({ world });
      }
    },
    [setDollhouse, setFsetupStore, world]
  );

  return view === ViewModeEnum.Outlines ? (
    model && <Projection url={model} />
  ) : (
    <Suspense
      fallback={placeholder ? <InternalSpace ref={setScene} url={placeholder} /> : <Loader />}
    >
      {model && <InternalSpace ref={setScene} name="space" url={model} />}
    </Suspense>
  );
};
